import { IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { useFormikContext } from 'formik';
import { DocumentNode } from 'graphql';
import React from 'react';

import { GetPaymentDetails_payment } from 'ap/paymentTracking/view/PaymentTrackingView/__generated__/GetPaymentDetails';
import { PaymentAvailableDocumentTypes } from 'ap/paymentTracking/view/PaymentTrackingView/__generated__/PaymentAvailableDocumentTypes';
import {
  AttachablePaymentDocuments,
  AttachablePaymentDocuments_attachablePaymentDocuments_nodes,
} from 'ap/paymentTracking/view/PaymentTrackingView/BasicForm/PaymentDocuments/__generated__/AttachablePaymentDocuments';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { _columns } from 'common/components/ActionMenu/Attach/payment_columns_data';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { TABLE_ROWS } from 'common/constants';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import { useStyles } from './index.styles';
import { toOrderByVariable } from './utils';

// Load GraphQL queries and mutations
import ATTACHABLE_PAYMENT_DOCUMENTS from '../../../../BasicForm/PaymentDocuments/AttachablePaymentDocuments.graphql';
import ATTACH_PAYMENT_DOCUMENTS from '../../../../BasicForm/PaymentDocuments/AttachPaymentDocument.graphql';
import PAYMENT_DETAILS from '../../../../GetPaymentDetails.graphql';

type AttachableDataType =
  AttachablePaymentDocuments_attachablePaymentDocuments_nodes;

interface AttachFormProps {
  AvailableDocumentTypes: PaymentAvailableDocumentTypes | undefined;
  payment?: GetPaymentDetails_payment;
}

export const AttachForm: React.FC<AttachFormProps> = ({
  AvailableDocumentTypes,
  payment,
}) => {
  const { dirty } = useFormikContext();
  const styles = useStyles();

  if (!payment) return null;

  return (
    <GenericAttachForm
      entity={payment}
      isDirty={dirty}
      availableDocumentTypes={AvailableDocumentTypes}
      attachableDocumentsQuery={ATTACHABLE_PAYMENT_DOCUMENTS as DocumentNode}
      attachDocumentsMutation={ATTACH_PAYMENT_DOCUMENTS as DocumentNode}
      detailsQuery={PAYMENT_DETAILS as DocumentNode}
      columns={_columns}
      modalWidth={1350}
      getDocumentTypeOptions={(availableDocumentTypes) => {
        return (
          availableDocumentTypes?.paymentAvailableDocumentTypes?.nodes.map(
            (doctype) => ({
              key: doctype.id,
              text: doctype.documentType || '',
              title:
                !payment?.payCycle?.isDraft && doctype.isAccountingDocument
                  ? 'Upload prohibited during/after the approval process'
                  : doctype.documentType || '',
              disabled:
                (!payment?.payCycle?.isDraft && doctype.isAccountingDocument) ||
                false,
            })
          ) || []
        );
      }}
      transformData={(data: AttachablePaymentDocuments | undefined) => {
        return data?.attachablePaymentDocuments?.nodes || [];
      }}
      canSelectItem={(item: AttachableDataType) => {
        const canSelect = payment?.payCycle?.isDraft
          ? true
          : !item._isAccountingDocument;
        return canSelect;
      }}
      getMutationInput={(
        selectedItems: AttachableDataType[],
        entityId: string
      ) => {
        return {
          input: {
            paymentId: entityId,
            entityDocumentId: selectedItems.map((item) => item.id),
          },
        };
      }}
      onRenderItemColumn={(item, _index, column, openDocumentViewModal) => {
        if (!item || !column) return undefined;

        const disableLogic = payment?.payCycle?.isDraft
          ? false
          : item._isAccountingDocument;

        const fieldContent = item[
          column.fieldName as keyof AttachableDataType
        ] as string | null;

        switch (column.key) {
          case 'fileReference':
            return (
              <Stack className={styles.columnHeight} verticalAlign="center">
                <DocumentDataCallout disabled={disableLogic!} item={item} />
              </Stack>
            );
          case 'documentType':
            return (
              <Stack className={styles.columnHeight} verticalAlign="center">
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {item.documentTypes?.documentType}
                </Text>
              </Stack>
            );
          case 'indexAmount':
            return (
              <Stack
                verticalAlign="center"
                className={styles.onrenderColumnStack}
              >
                <Text className={styles.contentColumnAlignRight}>
                  {fieldContent}
                </Text>
              </Stack>
            );
          case 'isoCode':
            return (
              <Stack className={styles.columnHeight} verticalAlign="center">
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {item.currency?.isoCode}
                </Text>
              </Stack>
            );
          case '_uploadDate':
            return (
              <Stack className={styles.columnHeight} verticalAlign="center">
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {getGlobalDateFormat(item._uploadDate!)}
                </Text>
              </Stack>
            );
          case 'action':
            const viewDocumentVisible =
              item._isProtected! || item._fileViewer !== 'browser';
            return (
              <Stack
                className={styles.columnHeight}
                tokens={{ childrenGap: 10 }}
                horizontal
                verticalAlign="center"
              >
                <TooltipHost content="View" id="tooltipId">
                  <IconButton
                    disabled={viewDocumentVisible}
                    iconProps={{ iconName: 'View' }}
                    onClick={() =>
                      openDocumentViewModal?.({
                        isOpen: true,
                        title: item.fileReference,
                        entityDocumentId: item.id,
                        _fileType: item._fileType!,
                      })
                    }
                  />
                </TooltipHost>
              </Stack>
            );
          case 'indexTransactionDate':
            return (
              <Stack className={styles.columnHeight} verticalAlign="center">
                {item.indexTransactionDate && (
                  <Text className={disableLogic ? styles.disabledText : ''}>
                    {dateFormat(dateConvertions(item.indexTransactionDate))}
                  </Text>
                )}
              </Stack>
            );
          default:
            return (
              <Stack className={styles.columnHeight} verticalAlign="center">
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {fieldContent}
                </Text>
              </Stack>
            );
        }
      }}
      initialQueryVariables={{
        payCycleId: payment?.payCycleId || '',
        first: TABLE_ROWS,
        orderBy: ['_UPLOAD_DATE_DESC', 'PRIMARY_KEY_ASC'],
      }}
      toOrderByVariable={toOrderByVariable}
    />
  );
};
